import { useEffect, useState } from 'react'
import { CheckCircle2, ClipboardList, LoaderCircle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useCreatedCards } from '../hooks/useCreatedCards'
import EmptyState from './ui/EmptyState'

const VIEWS = {
    open: { icon: ClipboardList, cardsKey: 'openCards' },
    completed: { icon: CheckCircle2, cardsKey: 'completedCards' },
}

function formatCreatedAt(value, locale) {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(locale || 'ro', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date)
}

export default function CreatedCardsCenter({ onOpenCard }) {
    const { t, i18n } = useTranslation()
    const { user, profile } = useAuth()
    const { openCards, completedCards, updatedCardIds, loading, markCardSeen } = useCreatedCards(user?.id, profile?.company_id)
    const [activeView, setActiveView] = useState(null)
    const cardsByView = { openCards, completedCards }
    const updatedIds = new Set(updatedCardIds)

    useEffect(() => {
        if (!activeView) return undefined
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setActiveView(null)
        }
        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [activeView])

    const openCard = async (cardId) => {
        markCardSeen(cardId)
        setActiveView(null)
        await onOpenCard?.(cardId, { preload: true })
    }

    const activeConfig = activeView ? VIEWS[activeView] : null
    const activeCards = activeConfig ? cardsByView[activeConfig.cardsKey] : []
    const ActiveIcon = activeConfig?.icon || ClipboardList

    return (
        <div className="created-cards-center">
            {Object.entries(VIEWS).map(([view, config]) => {
                const Icon = config.icon
                const cards = cardsByView[config.cardsKey]
                const updatedCount = cards.filter((card) => updatedIds.has(card.id)).length
                return (
                    <button
                        key={view}
                        type="button"
                        className={`created-cards-button is-${view}${activeView === view ? ' is-active' : ''}`}
                        onClick={() => setActiveView((current) => current === view ? null : view)}
                        aria-expanded={activeView === view}
                        aria-label={t(`createdCards.${view}.open`)}
                        title={t(`createdCards.${view}.title`)}
                    >
                        <Icon size={20} />
                        <span className="created-cards-count">{cards.length > 99 ? '99+' : cards.length}</span>
                        {updatedCount ? (
                            <span
                                className="created-cards-update-dot"
                                title={t('createdCards.updatedCount', { count: updatedCount })}
                                aria-label={t('createdCards.updatedCount', { count: updatedCount })}
                            />
                        ) : null}
                    </button>
                )
            })}

            {activeView ? (
                <>
                    <div className="notif-backdrop" onClick={() => setActiveView(null)} />
                    <section className="notif-panel created-cards-panel" aria-label={t(`createdCards.${activeView}.title`)}>
                        <div className="notif-header">
                            <div className="notif-title-group">
                                <ActiveIcon size={17} />
                                <span className="notif-title">{t(`createdCards.${activeView}.title`)}</span>
                                <span className="notif-count">({activeCards.length})</span>
                            </div>
                            <button type="button" className="notif-close" onClick={() => setActiveView(null)} aria-label={t('common.close')}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="notif-list">
                            {loading ? (
                                <div className="created-cards-loading"><LoaderCircle size={22} className="spin" /></div>
                            ) : !activeCards.length ? (
                                <EmptyState
                                    icon={<ActiveIcon size={38} style={{ color: 'var(--text-muted)', opacity: 0.45 }} />}
                                    title={t(`createdCards.${activeView}.emptyTitle`)}
                                    description={t(`createdCards.${activeView}.emptyDescription`)}
                                />
                            ) : activeCards.map((card) => (
                                <button
                                    type="button"
                                    key={card.id}
                                    className={`notif-item created-card-item${updatedIds.has(card.id) ? ' has-update' : ''}`}
                                    onClick={() => { void openCard(card.id) }}
                                >
                                    <span className="notif-icon"><ActiveIcon size={16} /></span>
                                     <span className="notif-content">
                                         <strong className="notif-item-title">{card.title}</strong>
                                     </span>
                                     <time className="created-card-date" dateTime={card.created_at || undefined}>
                                         {formatCreatedAt(card.created_at, i18n.resolvedLanguage)}
                                     </time>
                                     {updatedIds.has(card.id) ? (
                                        <span className="created-card-update-dot" title={t('createdCards.updated')} />
                                    ) : null}
                                </button>
                            ))}
                        </div>
                    </section>
                </>
            ) : null}
        </div>
    )
}
